import { supabase } from '@/integrations/supabase/client';
import { IAudioEngine, AudioState, AudioConfig, StateChangeCallback } from './IAudioEngine';

/**
 * Audio engine for pre-recorded MP3 files from database
 * Uses HTMLAudioElement for playback with full control support
 */
export class RecordedAudioEngine implements IAudioEngine {
  private audioElement: HTMLAudioElement | null = null;
  private isDestroyed: boolean = false; // Prevent stale callbacks after destroy
  private state: AudioState = {
    isPlaying: false,
    isPaused: false,
    isStopped: true,
    currentTime: 0,
    duration: 0,
    progress: 0,
    currentWordIndex: 0,
    error: null,
    isReady: false,
    bufferingProgress: 0,
  };
  private config: AudioConfig = {
    rate: 1,
    pitch: 1,
    volume: 0.8,
  };
  private callbacks: StateChangeCallback[] = [];
  private moduleId: string = '';
  private sectionId: string = '';
  private userId: string = '';
  private progressSaveTimer: number | null = null;
  
  // Error recovery with exponential backoff
  private loadAttempts: number = 0;
  private maxLoadAttempts: number = 3;
  private currentAudioUrl: string = '';

  // Store bound event handlers for proper cleanup
  private boundHandlers: {
    loadedmetadata?: () => void;
    canplaythrough?: () => void;
    progress?: () => void;
    timeupdate?: () => void;
    ended?: () => void;
    error?: (e: Event) => void;
    play?: () => void;
    pause?: () => void;
  } = {};

  /**
   * PHASE 4: Initialize with pre-fetched URL for instant playback
   */
  async initializeWithUrl(audioUrl: string, moduleId: string, sectionId: string, config?: Partial<AudioConfig>) {
    // CRITICAL: Validate audioUrl before anything else
    if (!audioUrl || typeof audioUrl !== 'string' || !audioUrl.startsWith('http')) {
      console.error('❌ RecordedAudioEngine: Invalid audio URL:', audioUrl);
      this.state.error = 'Invalid audio URL';
      this.notifyStateChange();
      return;
    }

    console.log('🎵 RecordedAudioEngine: Fast initialization with pre-fetched URL');
    
    this.isDestroyed = false; // Reset destroyed flag on new init
    this.moduleId = moduleId;
    this.sectionId = sectionId;
    
    if (config) {
      this.config = { ...this.config, ...config };
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      this.userId = user.id;
    }

    // Reset retry counter and store URL for retries
    this.loadAttempts = 0;
    this.currentAudioUrl = audioUrl;
    
    // Clear any previous error state and reset ready state
    this.state.error = null;
    this.state.isReady = false;
    this.state.bufferingProgress = 0;
    
    // Create audio element WITHOUT src first - CRITICAL for CORS to work
    this.audioElement = new Audio();
    this.audioElement.crossOrigin = 'anonymous'; // Must be set BEFORE src!
    this.audioElement.volume = this.config.volume;
    this.audioElement.playbackRate = this.config.rate;
    this.audioElement.preload = 'auto';

    // Set up event listeners BEFORE setting src
    this.setupAudioListeners();

    // NOW set src and load - after CORS and listeners are ready
    this.audioElement.src = audioUrl;
    this.audioElement.load();
    console.log('🔄 RecordedAudioEngine: Loading audio...');
    
    // PHASE 3: Optimistic ready for cached Supabase audio
    if (audioUrl.includes('supabase.co/storage')) {
      setTimeout(() => {
        if (this.isDestroyed) return; // Don't update if destroyed
        if (!this.state.error && !this.state.isReady && this.audioElement) {
          console.log('⚡ RecordedAudioEngine: Optimistic ready (cached audio)');
          this.state.isReady = true;
          this.state.bufferingProgress = 100;
          this.notifyStateChange();
        }
      }, 500);
    }

    // Load last position from DB (async, non-blocking)
    this.loadLastPosition().catch(err => {
      console.warn('Failed to load last position:', err);
    });
  }

  async initialize(audioUrl: string, moduleId: string, sectionId: string, config?: Partial<AudioConfig>) {
    return this.initializeWithUrl(audioUrl, moduleId, sectionId, config);
  }

  private setupAudioListeners() {
    if (!this.audioElement) return;

    // Create bound handlers so we can remove them later
    this.boundHandlers.loadedmetadata = () => {
      if (this.isDestroyed || !this.audioElement) return;
      this.state.duration = this.audioElement.duration;
      this.state.error = null;
      this.loadAttempts = 0;
      console.log('✅ RecordedAudioEngine: Metadata loaded, duration:', this.state.duration);
      this.notifyStateChange();
    };

    this.boundHandlers.canplaythrough = () => {
      if (this.isDestroyed || !this.audioElement) return;
      this.state.isReady = true;
      this.state.error = null;
      this.state.bufferingProgress = 100;
      console.log('✅ RecordedAudioEngine: Audio READY to play!');
      this.notifyStateChange();
    };

    this.boundHandlers.progress = () => {
      if (this.isDestroyed || !this.audioElement || !this.state.duration) return;
      
      const buffered = this.audioElement.buffered;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        const bufferProgress = Math.min(100, (bufferedEnd / this.state.duration) * 100);
        this.state.bufferingProgress = Math.round(bufferProgress);
        this.notifyStateChange();
      }
    };

    this.boundHandlers.timeupdate = () => {
      if (this.isDestroyed || !this.audioElement) return;
      this.state.currentTime = this.audioElement.currentTime;
      this.state.progress = this.state.duration > 0 
        ? (this.state.currentTime / this.state.duration) * 100 
        : 0;
      this.notifyStateChange();
      this.scheduleSaveProgress();
    };

    this.boundHandlers.ended = () => {
      if (this.isDestroyed) return;
      this.state.isPlaying = false;
      this.state.isPaused = false;
      this.state.isStopped = true;
      this.state.progress = 100;
      this.notifyStateChange();
      this.saveProgress(this.state.duration, 100);
    };

    this.boundHandlers.error = (e: Event) => {
      if (this.isDestroyed) return; // Ignore errors after destroy
      
      console.error('🎵 RecordedAudioEngine: Audio error details:', {
        error: this.audioElement?.error,
        networkState: this.audioElement?.networkState,
        src: this.audioElement?.src?.substring(0, 100) + '...',
        event: e
      });
      
      // Implement exponential backoff retry
      if (this.loadAttempts < this.maxLoadAttempts) {
        this.loadAttempts++;
        const retryDelay = 1000 * this.loadAttempts;
        console.log(`🔄 RecordedAudioEngine: Retry ${this.loadAttempts}/${this.maxLoadAttempts} in ${retryDelay}ms`);
        
        setTimeout(() => {
          if (this.isDestroyed) return; // Don't retry if destroyed
          if (this.audioElement && this.currentAudioUrl) {
            const bustUrl = this.currentAudioUrl + (this.currentAudioUrl.includes('?') ? '&' : '?') + `_retry=${Date.now()}`;
            this.audioElement.src = bustUrl;
            this.audioElement.load();
          }
        }, retryDelay);
        return;
      }
      
      // All retries failed
      const mediaError = this.audioElement?.error;
      let errorMessage = 'Failed to load audio file';
      
      if (mediaError) {
        switch (mediaError.code) {
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio file not found or format not supported';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error - please check connection';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio file is corrupted';
            break;
        }
      }
      
      this.state.error = `[FINAL] ${errorMessage}`;
      this.state.isStopped = true;
      console.error(`❌ RecordedAudioEngine: All ${this.maxLoadAttempts} retries exhausted - ${errorMessage}`);
      this.notifyStateChange();
    };

    this.boundHandlers.play = () => {
      if (this.isDestroyed) return;
      this.state.isPlaying = true;
      this.state.isPaused = false;
      this.state.isStopped = false;
      this.notifyStateChange();
    };

    this.boundHandlers.pause = () => {
      if (this.isDestroyed || !this.audioElement) return;
      if (this.audioElement.currentTime < this.audioElement.duration) {
        this.state.isPaused = true;
        this.state.isPlaying = false;
      }
      this.notifyStateChange();
    };

    // Attach all listeners
    this.audioElement.addEventListener('loadedmetadata', this.boundHandlers.loadedmetadata);
    this.audioElement.addEventListener('canplaythrough', this.boundHandlers.canplaythrough);
    this.audioElement.addEventListener('progress', this.boundHandlers.progress);
    this.audioElement.addEventListener('timeupdate', this.boundHandlers.timeupdate);
    this.audioElement.addEventListener('ended', this.boundHandlers.ended);
    this.audioElement.addEventListener('error', this.boundHandlers.error);
    this.audioElement.addEventListener('play', this.boundHandlers.play);
    this.audioElement.addEventListener('pause', this.boundHandlers.pause);
  }

  private removeAudioListeners() {
    if (!this.audioElement) return;

    // Remove all listeners BEFORE changing src
    if (this.boundHandlers.loadedmetadata) {
      this.audioElement.removeEventListener('loadedmetadata', this.boundHandlers.loadedmetadata);
    }
    if (this.boundHandlers.canplaythrough) {
      this.audioElement.removeEventListener('canplaythrough', this.boundHandlers.canplaythrough);
    }
    if (this.boundHandlers.progress) {
      this.audioElement.removeEventListener('progress', this.boundHandlers.progress);
    }
    if (this.boundHandlers.timeupdate) {
      this.audioElement.removeEventListener('timeupdate', this.boundHandlers.timeupdate);
    }
    if (this.boundHandlers.ended) {
      this.audioElement.removeEventListener('ended', this.boundHandlers.ended);
    }
    if (this.boundHandlers.error) {
      this.audioElement.removeEventListener('error', this.boundHandlers.error);
    }
    if (this.boundHandlers.play) {
      this.audioElement.removeEventListener('play', this.boundHandlers.play);
    }
    if (this.boundHandlers.pause) {
      this.audioElement.removeEventListener('pause', this.boundHandlers.pause);
    }
    
    this.boundHandlers = {};
  }

  async play(): Promise<void> {
    if (!this.audioElement) {
      console.warn('🎵 RecordedAudioEngine: No audio element');
      return;
    }
    
    try {
      await this.audioElement.play();
      console.log('🎵 RecordedAudioEngine: Playing');
    } catch (error) {
      console.error('🎵 RecordedAudioEngine: Play failed:', error);
      this.state.error = 'Failed to play audio';
      this.notifyStateChange();
    }
  }

  pause() {
    if (!this.audioElement) return;
    this.audioElement.pause();
    this.saveProgress(this.state.currentTime, this.state.progress);
    console.log('🎵 RecordedAudioEngine: Paused');
  }

  stop() {
    if (!this.audioElement) return;
    this.audioElement.pause();
    this.audioElement.currentTime = 0;
    this.state.isPlaying = false;
    this.state.isPaused = false;
    this.state.isStopped = true;
    this.state.currentTime = 0;
    this.state.progress = 0;
    this.notifyStateChange();
    console.log('🎵 RecordedAudioEngine: Stopped');
  }

  seekTo(time: number) {
    if (!this.audioElement) return;
    const clampedTime = Math.max(0, Math.min(time, this.state.duration));
    this.audioElement.currentTime = clampedTime;
    this.state.currentTime = clampedTime;
    this.state.progress = this.state.duration > 0 
      ? (clampedTime / this.state.duration) * 100 
      : 0;
    this.notifyStateChange();
    this.saveProgress(clampedTime, this.state.progress);
    console.log('🎵 RecordedAudioEngine: Seeked to', clampedTime);
  }

  setRate(rate: number) {
    const clampedRate = Math.max(0.5, Math.min(2, rate));
    this.config.rate = clampedRate;
    if (this.audioElement) {
      this.audioElement.playbackRate = clampedRate;
    }
    console.log('🎵 RecordedAudioEngine: Rate set to', clampedRate);
  }

  setVolume(volume: number) {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.config.volume = clampedVolume;
    if (this.audioElement) {
      this.audioElement.volume = clampedVolume;
    }
    console.log('🎵 RecordedAudioEngine: Volume set to', clampedVolume);
  }

  getState(): AudioState {
    return { ...this.state };
  }

  onStateChange(callback: StateChangeCallback) {
    this.callbacks.push(callback);
  }

  private notifyStateChange() {
    if (this.isDestroyed) return; // Don't notify after destruction
    this.callbacks.forEach(cb => cb(this.getState()));
  }

  private async loadLastPosition() {
    if (!this.userId || !this.moduleId) return;

    try {
      const { data, error } = await supabase
        .from('audio_progress')
        .select('last_position, progress')
        .eq('user_id', this.userId)
        .eq('module_id', this.moduleId)
        .eq('section_id', this.sectionId)
        .maybeSingle();

      if (data && !error) {
        const position = data.last_position || 0;
        
        if (position > 3 && this.state.duration > 0 && position < this.state.duration - 5) {
          const seekWhenReady = () => {
            if (this.isDestroyed) return; // Don't seek if destroyed
            if (this.audioElement && this.state.isReady) {
              this.audioElement.currentTime = position;
              this.state.currentTime = position;
              this.state.progress = data.progress || 0;
              this.notifyStateChange();
              console.log('🎵 RecordedAudioEngine: ⏪ Resumed from saved position:', Math.round(position), 'seconds');
            } else if (this.audioElement) {
              setTimeout(seekWhenReady, 500);
            }
          };
          
          seekWhenReady();
        } else if (position > 0) {
          console.log('🎵 RecordedAudioEngine: Skipping resume (position too close to start/end):', position);
        }
      }
    } catch (error) {
      console.error('🎵 RecordedAudioEngine: Failed to load progress:', error);
    }
  }

  private scheduleSaveProgress() {
    if (this.progressSaveTimer) {
      clearTimeout(this.progressSaveTimer);
    }
    
    this.progressSaveTimer = window.setTimeout(() => {
      this.saveProgress(this.state.currentTime, this.state.progress);
    }, 2000);
  }

  private async saveProgress(position: number, progress: number) {
    if (!this.userId || !this.moduleId) return;

    try {
      await supabase
        .from('audio_progress')
        .upsert({
          user_id: this.userId,
          module_id: this.moduleId,
          section_id: this.sectionId,
          last_position: Math.round(position),
          progress: Math.min(100, Math.round(progress)),
          duration: Math.round(this.state.duration),
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,module_id,section_id'
        });
    } catch (error) {
      console.error('🎵 RecordedAudioEngine: Failed to save progress:', error);
    }
  }

  destroy() {
    // Mark as destroyed FIRST to prevent any callbacks
    this.isDestroyed = true;
    
    if (this.progressSaveTimer) {
      clearTimeout(this.progressSaveTimer);
    }
    
    if (this.audioElement) {
      // CRITICAL: Remove all listeners BEFORE changing src
      this.removeAudioListeners();
      
      // Now safe to stop and clear
      this.audioElement.pause();
      this.audioElement.src = '';
      this.audioElement = null;
    }
    
    this.callbacks = [];
    console.log('🎵 RecordedAudioEngine: Destroyed');
  }
}
